/**
 * Transforma as séries da Quick Stats (já normalizadas pela API em
 * { ano, mes, valor, previsao }) nos pontos dos minigráficos.
 *
 * Três leituras:
 *   anual     um ponto por ano (Crop Production)
 *   mesmoMes  o mesmo mês em cada ano — é como os relatórios mensais
 *             comparam (ex.: confinamento em 1º de agosto, 2021 a 2026)
 *   acumulado janeiro até o mês de referência; soma nas séries de fluxo e
 *             média nas de média. Estoque ("1º de agosto") e taxa (peso,
 *             leite por vaca — que o USDA pondera pelo rebanho) não
 *             acumulam e caem para mesmoMes.
 */

/** Séries em que o acumulado não bate com o número oficial. */
export const naoAcumula = (serie) => serie.tipo === "estoque" || serie.tipo === "taxa";

export const MESES_CURTOS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
export const MESES_LONGOS = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

const formatarNumero = (valor, decimais) =>
  Number(valor).toLocaleString("pt-BR", {
    minimumFractionDigits: decimais,
    maximumFractionDigits: decimais,
  });

/**
 * Escala comum a todos os pontos do gráfico, escolhida pelo maior valor.
 * Taxas (bushels/acre, libras/vaca) nunca são escaladas: "178,5" é mais
 * legível que "0,18 mil".
 */
export function escolherEscala(valores, unidade) {
  if (unidade.includes("/")) return { divisor: 1, prefixo: "" };
  const maior = Math.max(...valores.map((v) => Math.abs(v)));
  if (maior >= 1e9) return { divisor: 1e9, prefixo: "bilhões de " };
  if (maior >= 1e6) return { divisor: 1e6, prefixo: "milhões de " };
  if (maior >= 1e3) return { divisor: 1e3, prefixo: "mil " };
  return { divisor: 1, prefixo: "" };
}

const doisDigitos = (ano) => String(ano).slice(-2);

/**
 * Fator e unidade da série no sistema pedido. As conversões vêm do catálogo
 * da API (`metrica`); séries sem conversão, como cabeças, ficam como estão.
 */
export function unidadeDaSerie(serie, metrico) {
  if (metrico && serie.metrica) {
    return { fator: serie.metrica.fator, unidade: serie.metrica.unidade, decimais: serie.metrica.decimais };
  }
  return { fator: 1, unidade: serie.unidade, decimais: serie.decimais };
}

const ABREVIACOES = { toneladas: "t", hectares: "ha", cabeças: "cab.", libras: "lb", acres: "acres", bushels: "bu" };
/** "milhões de toneladas" -> "milhões de t", para caber no título da imagem. */
export const abreviarUnidade = (texto) =>
  texto.replace(/\b(toneladas|hectares|cabeças|libras|bushels)\b/g, (u) => ABREVIACOES[u]);

/** Último mês com dado no ano mais recente da série. */
export function ultimoMesDisponivel(pontos) {
  const mensais = pontos.filter((p) => p.mes);
  if (!mensais.length) return null;
  const ano = Math.max(...mensais.map((p) => p.ano));
  return Math.max(...mensais.filter((p) => p.ano === ano).map((p) => p.mes));
}

/**
 * Monta um minigráfico.
 * @returns {{ pontos, modo, mes, periodoTitulo, linhaExtra, unidadeTexto } | { vazio: true, motivo }}
 */
export function montarGrafico(serie, dados, { anos = 6, modo = "mesmoMes", mes = null, metrico = false } = {}) {
  const unidade = unidadeDaSerie(serie, metrico);
  const todos = (dados?.pontos || []).map((p) => ({ ...p, valor: p.valor * unidade.fator }));
  if (!todos.length) return { vazio: true, motivo: dados?.erro || "sem-dados" };

  let brutos;
  let modoUsado = serie.tipo === "anual" ? "anual" : modo;
  if (modoUsado === "acumulado" && naoAcumula(serie)) modoUsado = "mesmoMes";
  let mesRef = null;

  if (modoUsado === "anual") {
    brutos = todos
      .filter((p) => p.mes === null || p.mes === undefined)
      .map((p) => ({ ano: p.ano, valor: p.valor, previsao: p.previsao }));
  } else {
    mesRef = mes || ultimoMesDisponivel(todos);
    if (!mesRef) return { vazio: true, motivo: "sem-dados" };

    const porAno = new Map();
    for (const p of todos) {
      if (!p.mes) continue;
      if (!porAno.has(p.ano)) porAno.set(p.ano, new Map());
      porAno.get(p.ano).set(p.mes, p.valor);
    }

    brutos = [];
    for (const [ano, meses] of porAno) {
      if (modoUsado === "mesmoMes") {
        if (meses.has(mesRef)) brutos.push({ ano, valor: meses.get(mesRef) });
      } else {
        // Ano só entra com todos os meses de jan até a referência: um
        // acumulado com mês faltando pareceria uma queda que não houve.
        const valores = [];
        for (let m = 1; m <= mesRef; m++) if (meses.has(m)) valores.push(meses.get(m));
        if (valores.length !== mesRef) continue;
        const soma = valores.reduce((a, b) => a + b, 0);
        brutos.push({ ano, valor: serie.tipo === "media" ? soma / valores.length : soma });
      }
    }
  }

  brutos.sort((a, b) => a.ano - b.ano);
  const selecionados = brutos.slice(-anos);
  if (!selecionados.length) return { vazio: true, motivo: "sem-dados" };

  const escala = escolherEscala(selecionados.map((p) => p.valor), unidade.unidade);
  const decimais =
    escala.divisor > 1 ? 2 : unidade.decimais ?? (modoUsado === "acumulado" && serie.tipo === "media" ? 1 : 0);

  const pontos = selecionados.map((p) => {
    let safra;
    if (modoUsado === "anual") {
      // O último ponto fica colado na borda direita: um rótulo longo como
      // "2026 (prev. set)" sai cortado. A explicação vai na linha do período.
      safra = p.previsao ? `${p.ano}*` : String(p.ano);
    } else if (modoUsado === "mesmoMes") {
      safra = `${MESES_CURTOS[mesRef - 1]}/${doisDigitos(p.ano)}`;
    } else {
      safra = mesRef === 1 ? `jan/${doisDigitos(p.ano)}` : `jan-${MESES_CURTOS[mesRef - 1]}/${doisDigitos(p.ano)}`;
    }
    const valorEscalado = p.valor / escala.divisor;
    return {
      safra,
      anoNum: p.ano,
      valor: valorEscalado,
      valorFinal: valorEscalado,
      rotulo: formatarNumero(valorEscalado, decimais),
    };
  });

  const primeiro = selecionados[0].ano;
  const ultimo = selecionados[selecionados.length - 1].ano;
  const intervalo = primeiro === ultimo ? String(primeiro) : `${primeiro} a ${ultimo}`;
  let periodoTitulo = intervalo;
  if (modoUsado === "mesmoMes") {
    periodoTitulo =
      serie.tipo === "estoque"
        ? `1º de ${MESES_LONGOS[mesRef - 1]}, ${intervalo}`
        : `${MESES_LONGOS[mesRef - 1]}, ${intervalo}`;
  } else if (modoUsado === "acumulado") {
    const faixa = mesRef === 1 ? "jan" : `jan-${MESES_CURTOS[mesRef - 1]}`;
    periodoTitulo = `${serie.tipo === "media" ? "média" : "acumulado"} ${faixa}, ${intervalo}`;
  }

  const previsao = modoUsado === "anual" ? selecionados.find((p) => p.previsao) : null;
  const linhaExtra = previsao
    ? `${periodoTitulo} · *previsão de ${MESES_LONGOS[previsao.previsao - 1]} do USDA`
    : periodoTitulo;

  return {
    pontos,
    linhaExtra,
    modo: modoUsado,
    mes: mesRef,
    decimais,
    periodoTitulo,
    unidadeTexto: `${escala.prefixo}${unidade.unidade}`,
  };
}

/* ------------------------------------------------------------------ *
 * Ranking por estado
 * ------------------------------------------------------------------ */

const ESTADOS_PT = {
  ALABAMA: "Alabama", ALASKA: "Alasca", ARIZONA: "Arizona", ARKANSAS: "Arkansas",
  CALIFORNIA: "Califórnia", COLORADO: "Colorado", CONNECTICUT: "Connecticut", DELAWARE: "Delaware",
  FLORIDA: "Flórida", GEORGIA: "Geórgia", HAWAII: "Havaí", IDAHO: "Idaho", ILLINOIS: "Illinois",
  INDIANA: "Indiana", IOWA: "Iowa", KANSAS: "Kansas", KENTUCKY: "Kentucky", LOUISIANA: "Luisiana",
  MAINE: "Maine", MARYLAND: "Maryland", MASSACHUSETTS: "Massachusetts", MICHIGAN: "Michigan",
  MINNESOTA: "Minnesota", MISSISSIPPI: "Mississippi", MISSOURI: "Missouri", MONTANA: "Montana",
  NEBRASKA: "Nebraska", NEVADA: "Nevada", "NEW HAMPSHIRE": "New Hampshire", "NEW JERSEY": "Nova Jersey",
  "NEW MEXICO": "Novo México", "NEW YORK": "Nova Iorque", "NORTH CAROLINA": "Carolina do Norte",
  "NORTH DAKOTA": "Dakota do Norte", OHIO: "Ohio", OKLAHOMA: "Oklahoma", OREGON: "Oregon",
  PENNSYLVANIA: "Pensilvânia", "RHODE ISLAND": "Rhode Island", "SOUTH CAROLINA": "Carolina do Sul",
  "SOUTH DAKOTA": "Dakota do Sul", TENNESSEE: "Tennessee", TEXAS: "Texas", UTAH: "Utah",
  VERMONT: "Vermont", VIRGINIA: "Virgínia", WASHINGTON: "Washington", "WEST VIRGINIA": "Virgínia Ocidental",
  WISCONSIN: "Wisconsin", WYOMING: "Wyoming",
};

export const nomeEstado = (nome) =>
  ESTADOS_PT[nome] || String(nome).toLowerCase().replace(/(^|\s)\S/g, (l) => l.toUpperCase());

const capitalizar = (texto) => texto.charAt(0).toUpperCase() + texto.slice(1);

/** "Julho/2026", "1º de agosto/2026" ou "2026 (previsão de setembro)". */
export function textoPeriodo(serie, periodo) {
  if (!periodo) return "";
  if (!periodo.mes) {
    return periodo.previsao ? `${periodo.ano} (previsão de ${MESES_LONGOS[periodo.previsao - 1]})` : String(periodo.ano);
  }
  const mes = MESES_LONGOS[periodo.mes - 1];
  return serie.tipo === "estoque" ? `1º de ${mes}/${periodo.ano}` : `${capitalizar(mes)}/${periodo.ano}`;
}

/**
 * Monta o spec do Gerador ("barra-ranking") com os maiores estados.
 *
 * O total da caixa é a soma de TODOS os estados publicados, não só dos que
 * aparecem nas barras — e é rotulado como tal. No leite, por exemplo, o
 * relatório traz 24 estados: a soma deles (8,80 mi t em jul/2026) não é o
 * total dos Estados Unidos (9,14 mi t), e chamar de "Total EUA" publicaria
 * um número errado.
 */
export function montarRanking(serie, resposta, { top = 5, metrico = true, mostrarTotal = true } = {}) {
  const estados = resposta?.estados || [];
  if (!estados.length) return null;

  const unidade = unidadeDaSerie(serie, metrico);
  const convertidos = estados.map((e) => ({ estado: nomeEstado(e.estado), valor: e.valor * unidade.fator }));
  const selecionados = convertidos.slice(0, Math.max(1, top));

  // A escala sai dos valores exibidos; o total usa a mesma para os dois
  // números serem comparáveis de relance.
  const escala = escolherEscala(selecionados.map((e) => e.valor), unidade.unidade);
  const decimais = escala.divisor > 1 ? 2 : unidade.decimais ?? 0;
  const unidadeCurta = abreviarUnidade(`${escala.prefixo}${unidade.unidade}`);
  const total = convertidos.reduce((soma, e) => soma + e.valor, 0) / escala.divisor;
  // Vacas de cada estado se somam; peso por cabeça e produtividade por área não.
  const somavel = serie.tipo !== "taxa" && !unidade.unidade.includes("/");

  const titulo = `${serie.atributo} de ${serie.mercado}`;
  const periodo = textoPeriodo(serie, resposta.periodo);
  const formatar = (v) =>
    Number(v).toLocaleString("pt-BR", { minimumFractionDigits: decimais, maximumFractionDigits: decimais });

  return {
    modelo: "barra-ranking",
    titulo: [
      { texto: titulo, cor: "#F0F000" },
      { texto: " - EUA ", cor: "#F0F000" },
      { texto: `(${unidadeCurta})`, cor: "#FFFFFF", tamanho: 9 },
    ],
    subtitulo: [{ texto: `${estados.length} Principais Estados - ${periodo}`, cor: "#F0F000" }],
    fonte: "Fonte: RURAL BUSINESS/USDA",
    fundo: "#000000",
    fundoPlot: "nenhum",
    dados: {
      categorias: selecionados.map((e) => e.estado),
      series: [
        {
          nome: titulo,
          cor: "#4DA3FF",
          corFim: "#0B4FA8",
          valores: selecionados.map((e) => Number((e.valor / escala.divisor).toFixed(decimais))),
        },
      ],
    },
    legenda: { visivel: false },
    valores: { decimais, prefixo: "", sufixo: "", exibirEm: "todos" },
    rotulo: { corTexto: "#000000", corFundo: "#FFFFFF", corBorda: "#000000", opacidade: 1 },
    extras: {
      formato: "chato",
      caixaValor: true,
      caixas:
        mostrarTotal && somavel
          ? [
              {
                linhas: [`Total ${estados.length} estados:`, formatar(total), unidadeCurta],
                em: { fx: 0.66, fy: 0.56 },
                corFundo: "#1E7B34",
                corBorda: "#FFFFFF",
                corTexto: "#FFFFFF",
                opacidade: 1,
              },
            ]
          : [],
    },
  };
}
